import React, { FC, useEffect, useRef } from 'react'
import styles from './styles/TextArea.module.css'
import { getEntityMeta, useT } from '_metronic/i18n/Metronici18n'

interface ITextArea {
  id?: string
  value: string
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  rows?: number
  cols?: number
  className?: string
  isPlainText?: boolean
  shouldExpand?: boolean
  disabled?: boolean
  readOnly?: boolean
  isTextEnd?: boolean
  entity?: string
}

export const TextArea: FC<ITextArea> = ({
  id,
  value,
  onChange,
  onFocus,
  placeholder,
  rows = 5,
  cols = 30,
  className = '',
  isPlainText = false,
  shouldExpand = true,
  disabled = false,
  readOnly = false,
  isTextEnd = false,
  entity,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const { t } = useT()
  const meta = entity ? getEntityMeta(t, entity) : null

  useEffect(() => {
    const textarea = textAreaRef.current
    if (!textarea || !shouldExpand) return

    const resize = () => {
      textarea.style.height = 'auto'

      const minHeight = textarea.scrollHeight
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight || '20', 10)

      const minRowsHeight = lineHeight * rows

      textarea.style.height = `${Math.max(minHeight, minRowsHeight)}px`
    }

    resize()
    requestAnimationFrame(resize)
  }, [value, shouldExpand, rows])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !shouldExpand) e.preventDefault()
  }

  const resolvedPlaceholder =
    placeholder ??
    t(
      'placeholder.input',
      {
        name: meta?.labelLower,
        gender: meta?.gender,
      },
      'Preencha o campo...'
    )

  return (
    <textarea
      ref={textAreaRef}
      id={id}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onKeyDown={handleKeyDown}
      placeholder={resolvedPlaceholder}
      disabled={disabled}
      rows={rows}
      cols={cols}
      readOnly={readOnly}
      style={
        isPlainText && disabled
          ? { backgroundColor: 'transparent', borderColor: 'transparent' }
          : {}
      }
      className={`${styles.textarea} 
        ${shouldExpand ? 'fs-5' : 'fs-3 fw-bolder text-primary'} 
        ${isPlainText ? 'form-control-plaintext p-0' : 'form-control form-control-solid'} 
        ${isTextEnd ? 'text-end' : ''} 
        ${className}`}
    />
  )
}
